import { Button, Flex, Text } from "@mantine/core";
import {
  AddCircle,
  DocumentText,
  Lock,
  MinusCirlce,
  Wallet,
} from "iconsax-react";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useNotificationControl } from "../../context/notificationContext";
import { useAuth } from "../../context/auth";
import { IProductComplete } from "../../interfaces/Products/IProductDetail";
import { ERole } from "../../interfaces/User/newUser";
import { trackEvent } from "../../utils/AnalyticsGoogle/AnalyticsInit";
import {
  postAddInterest,
  postBuyPlan,
  useMyPlans,
  useMyProducts,
} from "../../hooks";
import { useMutation } from "@tanstack/react-query";
import { EPlanStatus, IMyPlansForDates } from "../../interfaces/MyPlans";
import { useTranslation } from "react-i18next";
import { client_selected } from "../../client";
import ModalLoading from "../Modals/ModalLoading";
import Modal from "../Modals/ModalResponse";
import { EServiceType } from "../../interfaces/MyPlans/IMyPlans";

interface IProps {
  selectedProduct: IProductComplete | undefined;
  isLoading?: boolean;
}

const BuyArea = ({ selectedProduct, isLoading }: IProps) => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const id = slug?.split("&id=")[1];
  const navigate = useNavigate();
  const location = useLocation();
  const { interest, setInterest, isAuthenticated, roleSelected } = useAuth();
  const { notificationControl } = useNotificationControl();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isOpenModalError, setIsOpenModalError] = useState<string>("");
  const roleSelectedIsAnalyst = roleSelected === ERole.analyst;
  const roleSelectedIsClient = roleSelected === ERole.client;
  const isGenial = client_selected.name === "Genial";

  const { data: dataMyProducts, isFetching: isFetchingMyProducts } =
    useMyProducts({
      enabled: roleSelectedIsAnalyst,
      active: EServiceType.All,
    });

  const { data: dataMyPlans, isFetching: isFetchingMyPlans } = useMyPlans({
    status: "",
    enabled: roleSelectedIsClient,
  });

  const isFetching = useMemo(() => {
    if (!isAuthenticated) return false;
    return roleSelectedIsClient ? isFetchingMyPlans : isFetchingMyProducts;
  }, [
    isAuthenticated,
    roleSelectedIsClient,
    isFetchingMyPlans,
    isFetchingMyProducts,
  ]);

  const productId = useMemo(() => Number(id), [id]);

  const isOwned = useMemo(() => {
    if (roleSelectedIsAnalyst) {
      return dataMyProducts?.data.some(
        (myProduct) => myProduct.id === productId
      );
    }
    return dataMyPlans?.dates.some((date) =>
      date.user_plans.some(
        (plan) =>
          plan.product.id === productId &&
          (plan.status === EPlanStatus.Approved ||
            plan.status === EPlanStatus.InProcessing)
      )
    );
  }, [roleSelectedIsAnalyst, dataMyProducts, dataMyPlans, productId]);

  const productSelected = useMemo(() => {
    if (!roleSelectedIsClient) return {} as IMyPlansForDates;
    return (
      dataMyPlans?.dates.find((date) =>
        date.user_plans.find(
          (plan) =>
            plan.product.id === productId &&
            (plan.status === EPlanStatus.Approved ||
              plan.status === EPlanStatus.InProcessing)
        )
      ) || ({} as IMyPlansForDates)
    );
  }, [roleSelectedIsClient, dataMyPlans, productId]);

  const selectedPlan = useMemo(
    () =>
      productSelected?.user_plans?.find(
        (plan) =>
          plan.product.id === productId &&
          (plan.status === EPlanStatus.Approved ||
            plan.status === EPlanStatus.InProcessing)
      ),
    [productSelected, productId]
  );

  const hasInterest = useMemo(() => {
    if (!interest.length || !selectedProduct) return false;
    return !!interest.find((i) => i.product.id === selectedProduct.product.id);
  }, [interest, selectedProduct]);

  const product = useMemo(() => {
    return selectedProduct ? selectedProduct : ({} as IProductComplete);
  }, [selectedProduct]);

  const { mutate } = useMutation(postAddInterest, {
    onSuccess: (data) => {
      // console.log(data);
    },
    onError: (error) => {
      // console.log(error);
    },
  });
  const { mutate: mutateBuyPlan } = useMutation(postBuyPlan, {
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: (errors: any) => {
      console.error(t("marketplace.comprar_produtos.error_buy"), errors);
      setIsOpenModalError(errors.response.data.message.toString() || "");
    },
  });

  const handleBuyProduct = useCallback(() => {
    // Rastrear evento de compra
    trackEvent(
      "Produto",
      "Iniciar Compra",
      `Produto: ${product?.product?.title} - ID: ${product?.product?.id}`
    );
    const buyProducts = "/comprar-servicos";
    const from = location.pathname;
    if (isGenial) {
      if (isAuthenticated) {
        setIsLoadingModal(true);

        mutate({ product_id: product.product.id });
        mutateBuyPlan({
          payment_info: null,
          payment_owner: null,
          plans: [
            {
              hasValue: !!product.plan[0].price,
              plan_id: product.plan[0].id,
              product_id: product.product.id,
            },
          ],
        });
        setIsLoadingModal(false);
      } else {
        mutate({ product_id: product.product.id });
        navigate("/autenticacao/acessar-conta", {
          state: { from: from },
        });
      }
    } else {
      setInterest((prevInterest) => {
        const productExists = prevInterest.some(
          (item) => item?.product?.id === product?.product?.id
        );

        if (productExists) {
          return prevInterest.map((item) =>
            item.product.id === product?.product?.id
              ? { ...item, selected: true }
              : { ...item, selected: false }
          );
        }

        return [
          ...prevInterest.map((item) => ({ ...item, selected: false })),
          { ...product, selected: true },
        ];
      });
      if (isAuthenticated) {
        navigate(buyProducts, { state: { from } });
      } else {
        mutate({ product_id: product?.product?.id });
        localStorage.setItem("redirect_path", buyProducts);
        navigate("/autenticacao/criar-conta", {
          state: { from: buyProducts },
        });
      }
    }
  }, [
    product,
    setInterest,
    location.pathname,
    isAuthenticated,
    navigate,
    mutate,
  ]);

  const handleAddToCart = useCallback(() => {
    if (isAuthenticated) {
      mutate({ product_id: product?.product?.id });
    }
    // Rastrear evento de adição à lista de interesses
    trackEvent(
      "Produto",
      "Adicionar à Lista de Interesse",
      `Produto: ${product?.product?.title} - ID: ${product?.product?.id}`
    );
    setInterest((prev) => [...prev, product]);
    notificationControl({
      message: t("marketplace.detalhe.handle_add_to_cart_message"),
      type: "check",
    });
  }, [isAuthenticated, mutate, product, setInterest, notificationControl, t]);

  const handleRemoveFromCart = useCallback(() => {
    setInterest(
      interest.filter(
        (interestProduct) => interestProduct.product.id !== product.product.id
      )
    );
    notificationControl({
      message: t("marketplace.detalhe.buy_area.product_remove_list"),
      type: "check",
    });
  }, [interest, product, setInterest, notificationControl, t]);

  const handleSucessConfirmationClose = () => {
    setIsLoadingModal(false);
    setIsSuccess(false);
  };
  const handleRedirect = () => {
    navigate("/meu-portfolio/minhas-compras");
  };
  const handleCloseErrorModal = () => {
    setIsOpenModalError("");
  };

  const renderAnalystOwned = () => (
    <Flex direction="column" gap="16px" style={{ flexGrow: 1 }}>
      <Text color="basic.5" fw={500} size={16}>
        {t("marketplace.detalhe.owner")}
      </Text>
      <Button
        leftIcon={<DocumentText size={24} />}
        variant="primary"
        onClick={() => navigate(`/meu-portfolio/meus-servicos`)}
        w="100%"
        loading={isFetching || isLoading}
      >
        {t("marketplace.detalhe.owner_see")}
      </Button>
    </Flex>
  );

  const renderClientOwned = () => (
    <Flex direction="column" gap="16px" style={{ flexGrow: 1 }}>
      <Text color="basic.5" fw={500} size={16}>
        {t("marketplace.detalhe.already_client")}
      </Text>
      <Button
        leftIcon={<DocumentText size={24} />}
        variant="primary"
        onClick={() =>
          navigate(
            `/meu-portfolio/minhas-compras/configuracao-do-produto/id=${selectedPlan?.id}`
          )
        }
        w="100%"
        loading={isFetching || isLoading}
        disabled={!selectedPlan}
      >
        {t("marketplace.detalhe.see_and_configure")}
      </Button>
    </Flex>
  );

  const renderInterestButton = () => {
    if (!isAuthenticated) return null;
    if (hasInterest) {
      return (
        <Flex
          style={{ flexGrow: 2 }}
          maw={{
            base: "100%",
            1300: "600px",
          }}
          miw={{ base: "100%", 991: "250px", 1400: "350px" }}
        >
          <Button
            leftIcon={<MinusCirlce size={24} />}
            variant="dangerOutline"
            w="100%"
            loading={isFetching || isLoading}
            onClick={handleRemoveFromCart}
          >
            {t("marketplace.detalhe.button_remove")}
          </Button>
        </Flex>
      );
    }
    return (
      <Flex
        style={{ flexGrow: 2 }}
        maw={{
          base: "100%",
          1300: "600px",
        }}
        miw={{ base: "100%", 991: "250px", 1400: "350px" }}
      >
        <Button
          leftIcon={<AddCircle size={24} />}
          variant="primary"
          onClick={handleAddToCart}
          w="100%"
          loading={isFetching || isLoading}
        >
          {t("marketplace.detalhe.button_add")}
        </Button>
      </Flex>
    );
  };

  return (
    <Flex
      gap="16px"
      w="100%"
      justify={"space-between"}
      wrap={{ base: "wrap", 1300: "nowrap" }}
    >
      {isLoadingModal ? (
        <ModalLoading open={isLoadingModal} />
      ) : (
        <Modal
          type={"success"}
          titleFirstButton={t(
            "marketplace.comprar_produtos.modal_success.title_first_button"
          )}
          titleSeccondButton={t("global.button.close")}
          description={t(
            "marketplace.comprar_produtos.modal_success.description"
          )}
          open={isSuccess}
          setClose={handleSucessConfirmationClose}
          secondClick={handleSucessConfirmationClose}
          firstClick={handleRedirect}
        />
      )}

      <Modal
        type={"danger"}
        titleFirstButton={t("global.button.close")}
        description={isOpenModalError}
        contentEspecial={
          <Text c="basic.4" fz="16px" lh="155%" ta={"center"} mb="32px">
            {t("marketplace.comprar_produtos.modal_danger.content_especial")}
          </Text>
        }
        textModal={t("marketplace.comprar_produtos.modal_danger.text_modal")}
        open={!!isOpenModalError}
        setClose={handleCloseErrorModal}
        firstClick={handleCloseErrorModal}
      />
      {roleSelectedIsAnalyst && !isOwned && (
        <Text color="basic.5" fw={500} size={16}>
          {t("marketplace.detalhe.only_clients")}
        </Text>
      )}

      {isOwned &&
        (roleSelectedIsAnalyst ? renderAnalystOwned() : renderClientOwned())}

      {!isOwned ? (
        isGenial ? (
          <Flex
            style={{ flexGrow: 2 }}
            maw={{
              base: "100%",
              1300: "100%",
            }}
            miw={{ base: "100%", 991: "250px", 1400: "350px" }}
          >
            <Button
              variant={!!isAuthenticated ? "primary" : "primaryOutline"}
              leftIcon={
                !!isAuthenticated ? <AddCircle size={24} /> : <Lock size={24} />
              }
              w="100%"
              onClick={handleBuyProduct}
              loading={isFetching}
            >
              {!!isAuthenticated
                ? t("global.button.assign")
                : t("marketplace.detalhe.button_assign_now_disabled")}
            </Button>
          </Flex>
        ) : (
          <>
            {renderInterestButton()}

            <Flex
              style={{ flexGrow: 2 }}
              maw={{
                base: "100%",
                1300: isAuthenticated ? "100%" : "600px",
              }}
              miw={{ base: "100%", 991: "250px", 1400: "350px" }}
            >
              <Button
                leftIcon={
                  !!isAuthenticated ? <Wallet size={24} /> : <Lock size={24} />
                }
                variant="primaryOutline"
                onClick={handleBuyProduct}
                w="100%"
                loading={isFetching || isLoading}
              >
                {!!isAuthenticated
                  ? t("marketplace.detalhe.button_buy_now")
                  : t("marketplace.detalhe.button_buy_now_disabled")}
              </Button>
            </Flex>
          </>
        )
      ) : null}
      {/* termos */}
      <Flex
        style={{ flexGrow: 1 }}
        maw={{
          base: "100%",
          1300: "350px",
        }}
        miw={{ base: "100%", 991: "250px", 1400: "350px" }}
      >
        <Button
          leftIcon={<DocumentText size={24} />}
          variant="primaryTransparent"
          component="a"
          target="_blank"
          w="100%"
          disabled={!!!selectedProduct?.product?.term_of_contract}
          href={selectedProduct?.product?.term_of_contract}
        >
          {t("marketplace.detalhe.terms")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default BuyArea;
